import { Link } from "react-router-dom";
import { useGameStore } from "../store/gameStore";
import GameCard from "../components/game/GameCard";
import { Search, PlusCircle, Ticket, ListChecks, Share2, Camera, BookOpen, Info, HelpCircle } from "lucide-react";

export default function HomePage() {
  const sessions = useGameStore((state) => state.sessions);

  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center pt-16 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
          <Search className="h-10 w-10 text-primary" />
        </div>
        <h1 className="mt-6 font-display text-5xl font-extrabold leading-tight text-text sm:text-6xl">
          I Spy
          <br />
          <span className="text-primary">For Real</span>
        </h1>
        <p className="mt-4 max-w-sm text-lg text-text-secondary">
          Create a scavenger hunt, invite friends, compete by finding items in
          the real world.
        </p>
        <Link
          to="/create"
          className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-primary px-8 py-4 text-lg font-semibold text-text-inverse transition-colors hover:bg-primary-hover"
        >
          <PlusCircle className="h-5 w-5" />
          Start a Game
        </Link>
        <Link
          to="/join"
          className="mt-3 inline-flex items-center gap-1.5 text-base font-medium text-text-secondary hover:text-primary"
        >
          <Ticket className="h-4 w-4" />
          Have an invite code? Join a game
        </Link>

        <div className="mt-16 w-full max-w-lg rounded-2xl bg-surface-sunken/60 px-6 py-8">
          <p className="mb-6 text-xs font-bold uppercase tracking-widest text-text-secondary">
            How it works
          </p>
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <ListChecks className="h-6 w-6 text-primary" />
              </div>
              <p className="mt-3 font-semibold text-text">Create</p>
              <p className="mt-1 text-sm leading-snug text-text-secondary">
                Pick items for your hunt
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
                <Share2 className="h-6 w-6 text-accent" />
              </div>
              <p className="mt-3 font-semibold text-text">Invite</p>
              <p className="mt-1 text-sm leading-snug text-text-secondary">
                Share the code with friends
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
                <Camera className="h-6 w-6 text-success" />
              </div>
              <p className="mt-3 font-semibold text-text">Play</p>
              <p className="mt-1 text-sm leading-snug text-text-secondary">
                Race to photograph every item
              </p>
            </div>
          </div>
        </div>

        <div className="mt-10 w-full max-w-lg">
          <p className="mb-4 text-center text-xs font-bold uppercase tracking-widest text-text-secondary">
            Learn More
          </p>
          <div className="grid grid-cols-3 gap-4">
            <Link
              to="/how-to-play"
              className="flex flex-col items-center rounded-2xl bg-surface p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <BookOpen className="h-6 w-6 text-primary" />
              </div>
              <p className="mt-3 text-sm font-semibold text-text">How to Play</p>
            </Link>
            <Link
              to="/about"
              className="flex flex-col items-center rounded-2xl bg-surface p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent/10">
                <Info className="h-6 w-6 text-accent" />
              </div>
              <p className="mt-3 text-sm font-semibold text-text">About</p>
            </Link>
            <Link
              to="/faq"
              className="flex flex-col items-center rounded-2xl bg-surface p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary/10">
                <HelpCircle className="h-6 w-6 text-secondary" />
              </div>
              <p className="mt-3 text-sm font-semibold text-text">FAQ</p>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-3xl font-extrabold text-text">
          Your Games
        </h2>
        <span className="text-sm font-bold text-text-secondary uppercase tracking-wide">
          {sessions.length} {sessions.length === 1 ? "game" : "games"}
        </span>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {sessions.map((session) => (
          <GameCard key={session.gameId} session={session} />
        ))}
      </div>
      <div className="mt-6 text-center">
        <Link
          to="/create"
          className="inline-flex items-center gap-2 rounded-2xl border-2 border-primary px-6 py-3 font-semibold text-primary transition-colors hover:bg-primary hover:text-text-inverse"
        >
          <PlusCircle className="h-4 w-4" />
          Start New Game
        </Link>
        <div className="mt-4 flex items-center justify-center gap-3 text-sm text-text-secondary">
          <Link to="/how-to-play" className="hover:text-primary">How to Play</Link>
          <span>|</span>
          <Link to="/about" className="hover:text-primary">About</Link>
          <span>|</span>
          <Link to="/faq" className="hover:text-primary">FAQ</Link>
        </div>
      </div>
    </div>
  );
}
